import { useWindowManager } from '../hooks/useWindowManager';
import { WindowManagerContext } from '../contexts/WindowManagerContext';
import { APP_REGISTRY } from '../appRegistry';
import DesktopIcon from './DesktopIcon';
import Window from './Window';
import Taskbar from './Taskbar';
import InternetExplorer from './InternetExplorer';
import MSNMessenger from './MSNMessenger';
import YouTubePlayer from './apps/YouTubePlayer';
import RufflePlayer from './apps/RufflePlayer';
import PinballApp from './apps/PinballApp';
import MyPicturesApp from './apps/MyPicturesApp';
import { useState } from 'react';

const DESKTOP_ICONS = [
  { id: 'ie', name: 'Internet Exploder', functional: true },
  { id: 'msn', name: 'MSN Misdemeanor', functional: true },
  { id: 'pinball', name: '3D Pinball', functional: true },
  { id: 'winamp', name: 'WinScam', functional: true },
  { id: 'faceleak', name: 'FaceLeak', functional: true },
  { id: 'pictures', name: 'My Pictures', functional: true },
  { id: 'recycle', name: 'Recycle Bin', functional: false },
];

export const Desktop = ({ playSound }) => {
  const [selectedIcon, setSelectedIcon] = useState(null);

  const {
    windows,
    activeWindowId,
    openWindow,
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    focusWindow,
    updateWindowTitle,
    handleTaskbarWindowClick,
  } = useWindowManager(playSound);

  const launchShortcut = (shortcutId) => {
    if (shortcutId === 'ie') {
      openWindow('ie', { url: 'portal', pageTitle: 'The Information Superhighway' });
    } else if (shortcutId === 'msn') {
      openWindow('msn', {});
    } else if (shortcutId === 'pinball') {
      openWindow('pinball', {});
    } else if (shortcutId === 'pictures') {
      openWindow('pictures', {});
    } else if (shortcutId === 'winamp') {
      openWindow('ie', { url: 'winampgalaxy', pageTitle: 'WinScam Galaxy' });
    } else if (shortcutId === 'faceleak') {
      openWindow('ie', { url: 'myface', pageTitle: 'FaceLeak' });
    }
  };

  const handleIconClick = (iconId, iconName, isFunctional) => {
    setSelectedIcon(iconId);
    if (!isFunctional) {
      playSound('error', 0.3);
      return;
    }
    launchShortcut(iconId);
  };

  const handleDesktopClick = (e) => {
    if (e.target.classList.contains('desktop')) {
      setSelectedIcon(null);
    }
  };

  const renderWindowContent = (win) => {
    switch (win.type) {
      case 'ie':
        return (
          <InternetExplorer
            windowId={win.id}
            initialUrl={win.props.url}
            initialYouToobVideoId={win.props.youToobVideoId}
            initialYouToobAutoplay={win.props.youToobAutoplay}
            playSound={playSound}
          />
        );
      case 'msn':
        return <MSNMessenger playSound={playSound} />;
      case 'youtube':
        return <YouTubePlayer videoId={win.props.videoId} title={win.props.title} />;
      case 'ruffle':
        return <RufflePlayer swfUrl={win.props.swfUrl} gameName={win.props.gameName} />;
      case 'pinball':
        return <PinballApp />;
      case 'pictures':
        return <MyPicturesApp />;
      default:
        return null;
    }
  };

  const contextValue = { openWindow, updateWindowTitle };

  return (
    <WindowManagerContext.Provider value={contextValue}>
      <div className="desktop" onClick={handleDesktopClick} data-testid="desktop">
        {/* Desktop Icons */}
        <div className="desktop-icons">
          {DESKTOP_ICONS.map((icon) => (
            <DesktopIcon
              key={icon.id}
              name={icon.name}
              isSelected={selectedIcon === icon.id}
              onClick={() => handleIconClick(icon.id, icon.name, icon.functional)}
            />
          ))}
        </div>

        {/* Windows */}
        {windows.map((win) => (
          <Window
            key={win.id}
            id={win.id}
            title={win.title}
            icon={win.icon}
            isActive={activeWindowId === win.id}
            isMinimized={win.isMinimized}
            isMaximized={win.isMaximized}
            zIndex={win.zIndex}
            onClose={() => closeWindow(win.id)}
            onMinimize={() => minimizeWindow(win.id)}
            onMaximize={() => maximizeWindow(win.id)}
            onFocus={() => focusWindow(win.id)}
            defaultWidth={APP_REGISTRY[win.type]?.defaultWidth}
            defaultHeight={APP_REGISTRY[win.type]?.defaultHeight}
          >
            {renderWindowContent(win)}
          </Window>
        ))}

        {/* Taskbar */}
        <Taskbar
          windows={windows}
          activeWindowId={activeWindowId}
          onWindowClick={handleTaskbarWindowClick}
          onStartClick={() => playSound('click', 0.2)}
          onStartLaunch={launchShortcut}
        />
      </div>
    </WindowManagerContext.Provider>
  );
};

export default Desktop;
